import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Paper,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  Grid,
  Alert,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  Mic as MicIcon,
  Stop as StopIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  AudioFile as AudioFileIcon,
  LocationOn as LocationIcon,
  ContactPhone as ContactIcon,
  StickyNote2 as StickerIcon,
  Movie as VideoIcon,
} from '@mui/icons-material';
import api from '../api';

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 100; // ~5 minutes

// Prefer OGG/Opus (accepted by WhatsApp). Otherwise the server converts webm with ffmpeg.
const pickRecorderMime = () => {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
  if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) return 'audio/ogg;codecs=opus';
  if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) return 'audio/webm;codecs=opus';
  return '';
};

const errorText = (err) => {
  const data = err.response?.data;
  if (data?.errors) return Object.values(data.errors).flat().join(' ');
  return data?.message || 'Failed to send.';
};

const SendWhatsAppModal = ({ open, onClose, selectedLeads, refreshLeads, onSent }) => {
  // template (default for bulk), text, image, video, audio, document, location, contact, sticker
  const [attachmentType, setAttachmentType] = useState('template');
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);

  // Specific Metadata States
  const [locData, setLocData] = useState({ latitude: '', longitude: '', name: '', address: '' });
  const [contactData, setContactData] = useState({ name: '', phone: '' });
  const [templateData, setTemplateData] = useState({ template_name: '', language: 'en_GB', body_params: [] });

  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);

  // Result of a send: { queued, skipped, missing_ids, batch_id } plus polled status
  const [result, setResult] = useState(null);
  const [batch, setBatch] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) clearTimeout(pollRef.current);
    pollRef.current = null;
  };

  useEffect(() => () => stopPolling(), []);

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    setTemplatesError('');
    try {
      const res = await api.get('/admin/whatsapp/templates');
      setTemplates(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      setTemplatesError(err.response?.data?.message || 'Could not load templates. You can type the template name instead.');
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && attachmentType === 'template' && !templates.length && !templatesLoading && !templatesError) {
      loadTemplates();
    }
  }, [open, attachmentType, templates.length, templatesLoading, templatesError, loadTemplates]);

  // --- Handlers ---

  const handleAttachClick = (event) => setAnchorEl(event.currentTarget);
  const handleAttachClose = () => setAnchorEl(null);

  const handleSelectType = (type) => {
    setAttachmentType(type);
    setFile(null);
    setAnchorEl(null);
    setError('');
  };

  const handleModeChange = (_e, mode) => {
    if (mode) handleSelectType(mode);
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const triggerFileSelect = (accept) => {
    if (fileInputRef.current) {
      fileInputRef.current.setAttribute('accept', accept);
      fileInputRef.current.click();
    }
  };

  const selectTemplate = (name, language) => {
    const t = templates.find((x) => x.name === name && x.language === language);
    setTemplateData({
      template_name: name,
      language: language || 'en_GB',
      body_params: Array(t?.body_param_count || 0).fill(''),
    });
  };

  const selectedTemplate = templates.find(
    (t) => t.name === templateData.template_name && t.language === templateData.language
  );

  // --- Recorder Logic ---
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = pickRecorderMime();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const type = (recorder.mimeType || mimeType || 'audio/webm').split(';')[0];
        const ext = type.includes('ogg') ? 'ogg' : 'webm';
        const audioBlob = new Blob(audioChunksRef.current, { type });
        const audioFile = new File([audioBlob], `voice_note.${ext}`, { type });
        setFile(audioFile);
        setAttachmentType('audio');
        stream.getTracks().forEach(track => track.stop());
      };

      recorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime(p => p + 1), 1000);
    } catch (err) {
      console.error(err);
      setError('Microphone access denied.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!['location', 'contact', 'template'].includes(attachmentType)) {
      setAttachmentType('text');
    }
  };

  const pollBatch = (batchId, count = 0) => {
    pollRef.current = setTimeout(async () => {
      try {
        const res = await api.get(`/admin/whatsapp/batches/${batchId}`);
        setBatch(res.data);
        if (!res.data.done && count < MAX_POLLS) pollBatch(batchId, count + 1);
        else if (refreshLeads) refreshLeads();
      } catch (err) {
        if (count < MAX_POLLS) pollBatch(batchId, count + 1);
      }
    }, POLL_INTERVAL_MS);
  };

  const validate = () => {
    if (attachmentType === 'template' && !templateData.template_name.trim()) return 'Choose a template.';
    if (attachmentType === 'template' && templateData.body_params.some((p) => !String(p).trim())) {
      return 'Fill in every template parameter.';
    }
    if (attachmentType === 'text' && !message.trim()) return 'Type a message.';
    if (['image', 'video', 'audio', 'document', 'sticker'].includes(attachmentType) && !file) return 'Choose a file.';
    if (attachmentType === 'location' && (locData.latitude === '' || locData.longitude === '')) return 'Latitude and longitude are required.';
    if (attachmentType === 'contact' && (!contactData.name || !contactData.phone)) return 'Contact name and phone are required.';
    return '';
  };

  const handleSend = async () => {
    const invalid = validate();
    if (invalid) {
      setError(invalid);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('lead_ids', JSON.stringify(selectedLeads));
      formData.append('type', attachmentType);

      // Metadata construction
      let meta = null;
      if (attachmentType === 'location') meta = locData;
      else if (attachmentType === 'contact') meta = contactData;
      else if (attachmentType === 'template') meta = templateData;

      if (meta) formData.append('metadata', JSON.stringify(meta));
      if (message && attachmentType !== 'template') formData.append('message', message);
      if (file) formData.append('file', file);

      // Let the browser set the multipart boundary.
      const res = await api.post('/admin/whatsapp/bulk-send', formData);

      setResult(res.data);
      setBatch(null);
      if (onSent) onSent(res.data);
      if (res.data.batch_id && res.data.queued > 0) {
        pollBatch(res.data.batch_id);
      } else if (res.data.batch_id) {
        // Nothing queued: load the failures straight away.
        const status = await api.get(`/admin/whatsapp/batches/${res.data.batch_id}`).catch(() => null);
        if (status) setBatch(status.data);
      }
    } catch (err) {
      console.error(err);
      setError(errorText(err));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    stopPolling();
    if (isRecording) stopRecording();
    setMessage('');
    setFile(null);
    setAttachmentType('template');
    setLocData({ latitude: '', longitude: '', name: '', address: '' });
    setContactData({ name: '', phone: '' });
    setTemplateData({ template_name: '', language: 'en_GB', body_params: [] });
    setError('');
    setResult(null);
    setBatch(null);
    onClose();
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  // --- Render Helpers ---

  const renderTemplateInputs = () => (
    <Box display="flex" flexDirection="column" gap={2}>
      {templatesLoading && <LinearProgress />}
      {templatesError && <Alert severity="warning">{templatesError}</Alert>}
      {templates.length > 0 ? (
        <TextField
          select fullWidth size="small" label="Approved template"
          value={selectedTemplate ? `${selectedTemplate.name}|${selectedTemplate.language}` : ''}
          onChange={(e) => {
            const [name, language] = e.target.value.split('|');
            selectTemplate(name, language);
          }}
        >
          {templates.map((t) => (
            <MenuItem key={`${t.name}|${t.language}`} value={`${t.name}|${t.language}`}>
              {t.name} ({t.language}){t.category ? ` - ${t.category}` : ''}
            </MenuItem>
          ))}
        </TextField>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={8}>
            <TextField fullWidth size="small" label="Template name" value={templateData.template_name}
              onChange={(e) => setTemplateData({ ...templateData, template_name: e.target.value.trim() })} />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth size="small" label="Language code" value={templateData.language}
              onChange={(e) => setTemplateData({ ...templateData, language: e.target.value.trim() })} />
          </Grid>
        </Grid>
      )}
      {selectedTemplate?.body && (
        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#f5f5f5', whiteSpace: 'pre-wrap' }}>
          <Typography variant="body2">{selectedTemplate.body}</Typography>
        </Paper>
      )}
      {selectedTemplate && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(selectedTemplate.header_format) && (
        <Alert severity="info">
          This template has a {selectedTemplate.header_format.toLowerCase()} header. Attach the file with the paperclip.
        </Alert>
      )}
      {templateData.body_params.map((p, i) => (
        <TextField key={i} fullWidth size="small" label={`Parameter {{${i + 1}}}`} value={p}
          onChange={(e) => {
            const next = [...templateData.body_params];
            next[i] = e.target.value;
            setTemplateData({ ...templateData, body_params: next });
          }} />
      ))}
      {!selectedTemplate && (
        <Button size="small" onClick={() => setTemplateData({ ...templateData, body_params: [...templateData.body_params, ''] })}>
          Add body parameter
        </Button>
      )}
      <Typography variant="caption" color="text.secondary">
        Templates can reach any lead. Free text only reaches people who messaged you in the last 24 hours.
      </Typography>
    </Box>
  );

  const renderInputs = () => {
    switch (attachmentType) {
      case 'template':
        return renderTemplateInputs();
      case 'location':
        return (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Latitude" value={locData.latitude} onChange={(e) => setLocData({ ...locData, latitude: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Longitude" value={locData.longitude} onChange={(e) => setLocData({ ...locData, longitude: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Location Name" value={locData.name} onChange={(e) => setLocData({ ...locData, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Address" value={locData.address} onChange={(e) => setLocData({ ...locData, address: e.target.value })} />
            </Grid>
          </Grid>
        );
      case 'contact':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Contact Name" value={contactData.name} onChange={(e) => setContactData({ ...contactData, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Phone Number" value={contactData.phone} onChange={(e) => setContactData({ ...contactData, phone: e.target.value })} />
            </Grid>
          </Grid>
        );
      case 'image':
      case 'video':
      case 'document':
        return (
          <TextField
            multiline rows={2} placeholder="Add a caption..."
            value={message} onChange={(e) => setMessage(e.target.value)}
            fullWidth
          />
        );
      case 'sticker':
        return <Typography variant="caption" color="text.secondary">Sticker selected (WebP, max 500 KB). No caption needed.</Typography>;
      case 'audio':
        return <Typography variant="caption" color="text.secondary">Audio is sent without a caption.</Typography>;
      default: // text
        return !isRecording && (
          <TextField
            multiline rows={4} placeholder="Type message..."
            value={message} onChange={(e) => setMessage(e.target.value)}
            fullWidth
          />
        );
    }
  };

  const renderResult = () => {
    const counts = batch?.counts;
    const failed = (batch?.details || []).filter((d) => d.status === 'failed');
    const pending = batch ? !batch.done : result.queued > 0;
    return (
      <Box display="flex" flexDirection="column" gap={2}>
        <Alert severity={result.queued > 0 ? 'info' : 'warning'}>
          {result.queued} queued
          {result.skipped?.length ? `, ${result.skipped.length} skipped (no or invalid phone)` : ''}
          {result.missing_ids?.length ? `, ${result.missing_ids.length} lead(s) not found` : ''}.
        </Alert>
        {pending && (
          <Box>
            <Typography variant="body2" gutterBottom>Sending... you can close this window; sending continues.</Typography>
            <LinearProgress />
          </Box>
        )}
        {counts && (
          <Box display="flex" gap={1} flexWrap="wrap">
            <Chip size="small" label={`Queued ${counts.queued}`} />
            <Chip size="small" color="primary" label={`Sent ${counts.sent}`} />
            <Chip size="small" color="success" label={`Delivered ${counts.delivered + counts.read}`} />
            <Chip size="small" color="error" label={`Failed ${counts.failed}`} />
          </Box>
        )}
        {failed.length > 0 && (
          <Paper variant="outlined" sx={{ maxHeight: 240, overflow: 'auto' }}>
            <List dense>
              {failed.map((d) => (
                <ListItem key={d.lead_id}>
                  <ListItemText
                    primary={`${d.name || `Lead #${d.lead_id}`} ${d.phone ? `(${d.phone})` : ''}`}
                    secondary={d.error || 'Failed'}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
        {result.missing_ids?.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            Not found: {result.missing_ids.join(', ')}
          </Typography>
        )}
      </Box>
    );
  };

  if (result) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>WhatsApp send results</DialogTitle>
        <DialogContent dividers>{renderResult()}</DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const isFreeForm = attachmentType !== 'template';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send WhatsApp - {attachmentType.toUpperCase()} ({selectedLeads.length} leads)</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2} minHeight={200}>
          <ToggleButtonGroup size="small" exclusive value={isFreeForm ? 'text' : 'template'} onChange={handleModeChange}>
            <ToggleButton value="template">Template</ToggleButton>
            <ToggleButton value="text">Free-form (24h window)</ToggleButton>
          </ToggleButtonGroup>

          {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

          {file && (
            <Paper variant="outlined" sx={{ p: 2, display: 'flex', justifyContent: 'space-between', bgcolor: '#f5f5f5' }}>
              <Box overflow="hidden"><Typography noWrap>{file.name}</Typography></Box>
              <IconButton size="small" onClick={handleRemoveFile}><DeleteIcon /></IconButton>
            </Paper>
          )}

          {isRecording && (
            <Box display="flex" alignItems="center" justifyContent="center" gap={2} p={2} bgcolor="#ffebee" borderRadius={1}>
              <MicIcon color="error" /> <Typography>Recording {formatTime(recordingTime)}</Typography>
              <IconButton onClick={stopRecording}><StopIcon /></IconButton>
            </Box>
          )}

          {renderInputs()}

        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: '#fafafa', justifyContent: 'space-between' }}>
        <Box>
          <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileSelect} />
          {attachmentType === 'template' ? (
            <Tooltip title="Header file (only for templates with a media header)">
              <IconButton onClick={() => triggerFileSelect('image/jpeg,image/png,video/mp4,application/pdf')} disabled={loading} color="primary">
                <AttachFileIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Attachments">
              <IconButton onClick={handleAttachClick} disabled={loading || isRecording} color="primary">
                <AttachFileIcon />
              </IconButton>
            </Tooltip>
          )}
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleAttachClose}>
            <MenuItem onClick={() => { handleSelectType('image'); triggerFileSelect('image/jpeg,image/png'); }}>
              <ListItemIcon><ImageIcon /></ListItemIcon> Image/Camera
            </MenuItem>
            <MenuItem onClick={() => { handleSelectType('video'); triggerFileSelect('video/mp4,video/3gpp'); }}>
              <ListItemIcon><VideoIcon /></ListItemIcon> Video
            </MenuItem>
            <MenuItem onClick={() => { handleSelectType('document'); triggerFileSelect('.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt'); }}>
              <ListItemIcon><AttachFileIcon /></ListItemIcon> Document
            </MenuItem>
            <MenuItem onClick={() => { handleSelectType('audio'); triggerFileSelect('audio/ogg,audio/mpeg,audio/mp4,audio/aac,audio/amr'); }}>
              <ListItemIcon><AudioFileIcon /></ListItemIcon> Audio File
            </MenuItem>
            <MenuItem onClick={() => handleSelectType('location')}>
              <ListItemIcon><LocationIcon /></ListItemIcon> Location
            </MenuItem>
            <MenuItem onClick={() => handleSelectType('contact')}>
              <ListItemIcon><ContactIcon /></ListItemIcon> Contact
            </MenuItem>
            <MenuItem onClick={() => { handleSelectType('sticker'); triggerFileSelect('image/webp'); }}>
              <ListItemIcon><StickerIcon /></ListItemIcon> Sticker
            </MenuItem>
          </Menu>
        </Box>

        <Box>
          {attachmentType === 'text' && !message && !file && !isRecording ? (
            <IconButton onClick={startRecording} color="primary" sx={{ bgcolor: '#e3f2fd' }}><MicIcon /></IconButton>
          ) : (
            <Button variant="contained" onClick={handleSend} disabled={loading || isRecording || !selectedLeads.length} endIcon={loading && <CircularProgress size={20} />}>
              Send
            </Button>
          )}
          <Button onClick={handleClose}>Cancel</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SendWhatsAppModal;
